import { NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useTheme } from '../../context/ThemeContext';
import { useToast } from '../../context/ToastContext';
import styles from './Sidebar.module.css';
import { useTranslation } from 'react-i18next';
import LanguageSwitcher from './LanguageSwitcher';

const NAV_ITEMS = [
  { path: '/dashboard', icon: '⬛', key: 'dashboard' },
  { path: '/subjects', icon: '📚', key: 'mySubjects' },
  { path: '/revisions', icon: '📅', key: 'revisions', badge: true },
  { path: '/progress', icon: '📊', key: 'progress' },
  { path: '/achievements', icon: '🏅', key: 'achievements' },
  { path: '/settings', icon: '⚙️', key: 'settings' },
];

const Sidebar = ({ revisionCount = 0, streak = 0 }) => {
  const { user, logout } = useAuth();
  const { t } = useTranslation();
  const { theme } = useTheme();
  const { success } = useToast();
  const navigate = useNavigate();

  const initials = user?.name
    ? user.name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)
    : 'ST';

  const handleLogout = async () => {
    await logout();
    success('Logged out successfully');
    navigate('/');
  };

  return (
    <aside className="sidebar">
      {/* Logo */}
      <div style={{ padding: '20px 16px 16px', borderBottom: '1px solid rgba(255,255,255,0.1)', display: 'flex', alignItems: 'center', gap: '10px' }}>
        {theme.logoUrl
          ? <img src={theme.logoUrl} alt="Logo" style={{ height: '36px', borderRadius: '8px' }} />
          : (
            <div style={{ width: '36px', height: '36px', borderRadius: '8px', background: 'var(--brand-primary)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: "'Baloo 2', cursive", fontSize: '16px', fontWeight: 800, color: 'white', flexShrink: 0 }}>
              G
            </div>
          )
        }
        <div>
          <div style={{ fontFamily: "'Baloo 2', cursive", fontSize: '13px', fontWeight: 800, color: 'var(--brand-primary)', lineHeight: 1.1 }}>MyPersonalGuruji</div>
          <div style={{ fontSize: '9px', color: 'rgba(255,255,255,0.4)', letterSpacing: '0.4px' }}>YOUR GURUJI. ALWAYS THERE.</div>
        </div>
      </div>

      {/* Student info */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '9px', whiteSpace: 'nowrap', padding: '10px 16px', borderBottom: '1px solid rgba(255,255,255,0.08)', overflow: 'hidden' }}>
        <div style={{ width: '34px', height: '34px', borderRadius: '50%', background: 'var(--brand-primary)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 800, fontSize: '12px', color: 'white', flexShrink: 0 }}>
          {initials}
        </div>
        <div>
          <div style={{ fontWeight: 700, fontSize: '12px', color: 'white' }}>{user?.name}</div>
          <div style={{ fontSize: '10px', color: 'rgba(255,255,255,0.45)' }}>Class {user?.classId} · CBSE</div>
        </div>
      </div>

      {/* Navigation */}
      <nav style={{ padding: '12px 10px', flex: 1, display: 'flex', flexDirection: 'column', gap: '2px' }}>
        {NAV_ITEMS.map(item => (
          <NavLink
            key={item.path}
            to={item.path}
            style={({ isActive }) => ({
              display: 'flex', alignItems: 'center', gap: '9px',
              padding: '8px 11px', borderRadius: 'var(--radius-sm)',
              color: isActive ? 'white' : 'rgba(255,255,255,0.55)',
              background: isActive ? 'var(--brand-primary)' : 'transparent',
              fontSize: '12px', fontWeight: 600, textDecoration: 'none',
              transition: 'all 0.15s'
            })}
          >
            <span style={{ fontSize: '14px', width: '16px', textAlign: 'center' }}>{item.icon}</span>
            {t('nav.' + item.key)}
            {item.badge && revisionCount > 0 && (
              <span style={{ marginLeft: 'auto', background: 'var(--red)', color: 'white', fontSize: '9px', fontWeight: 800, borderRadius: '10px', padding: '1px 5px' }}>
                {revisionCount}
              </span>
            )}
          </NavLink>
        ))}

        <LanguageSwitcher />
        {/* Logout */}
        <button
          onClick={handleLogout}
          style={{ display: 'flex', alignItems: 'center', gap: '9px', padding: '8px 11px', borderRadius: 'var(--radius-sm)', color: 'rgba(255,255,255,0.4)', background: 'transparent', border: 'none', fontSize: '12px', fontWeight: 600, cursor: 'pointer', marginTop: '8px', width: '100%' }}
        >
          <span style={{ fontSize: '14px', width: '16px', textAlign: 'center' }}>🚪</span>
          {t('nav.logout')}
        </button>
      </nav>

      {/* Streak */}
      {streak > 0 && (
        <div style={{ margin: '0 10px 4px', background: 'rgba(255,107,0,0.15)', border: '1px solid rgba(255,107,0,0.3)', borderRadius: 'var(--radius-sm)', padding: '9px 11px', display: 'flex', alignItems: 'center', gap: '8px' }}>
          <span style={{ fontSize: '18px' }}>🔥</span>
          <div>
            <div style={{ fontSize: '15px', fontWeight: 800, color: 'var(--brand-primary)' }}>{streak} Days</div>
            <div style={{ fontSize: '10px', color: 'rgba(255,255,255,0.45)' }}>Keep your streak alive!</div>
          </div>
        </div>
      )}
    </aside>
  );
};

export default Sidebar;
